/**
 * Financial Service
 * 
 * API service for the Librattum MAX financial module.
 */
import api from '@/lib/api';
import type {
  PendingCarryover,
  BudgetPeriod,
  BudgetPeriodNavigation,
  BudgetPeriodCreate,
  BudgetPeriodUpdate,
  BudgetPeriodSummary,
  Agent,
  AgentCreate,
  AgentUpdate,
  FinancialListItem,
  FinancialListItemCreate,
  FinancialListResponse,
  FinancialListType,
  Revenue,
  RevenueCreate,
  RevenueUpdate,
  RevenueRealization,
  FractionalRealizationCreate,
  FutureExpense,
  FutureExpenseCreate,
  FutureExpenseUpdate,
  FutureExpenseRealizationCreate,
  FutureExpenseRealizationUpdate,
  FutureExpenseRealization,
  FutureExpenseCalculation,
  FutureExpenseCalculationRequest,
  FutureExpenseExecuteRequest,
  FutureExpenseExecuteResponse,
  FutureExpenseRealizationSheet,
  InvestmentInstitution,
  InvestmentInstitutionCreate,
  InvestmentInstitutionUpdate,
  MonthlyExpense,
  MonthlyExpenseCreate,
  MonthlyExpenseUpdate,
  MonthlyExpenseRealization,
  DailyExpense,
  DailyExpenseCreate,
  DailyExpenseUpdate,
  DailyExpenseRealizationCreate,
  DailyExpenseRealizationUpdate,
  DailySuggestedSpend,
  DashboardData,
  PeriodSummaryReport,
  TrendAnalysisReport,
  ExpenseAnalysisReport,
  RevenueAnalysisReport,
  DashboardKPIs,
  CashFlowMode,
  CashFlowReport,
  FutureExpenseSummaryReport,
  ExpenseCategoryTimelineReport,
  RevenueTypeTimelineReport,
  FutureExpenseProjectionReport,
  UserFinancialSettings,
  UserFinancialSettingsUpdate,
  PaginatedResponse,
  RecentTransaction,
  SearchResults,
} from '@/types/financial';

const BASE_URL = '/financial';

const buildQueryParams = (params?: Record<string, string | string[] | undefined>) => {
  const searchParams = new URLSearchParams();
  if (!params) return searchParams;

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null) {
          searchParams.append(key, String(item));
        }
      });
      return;
    }
    searchParams.set(key, String(value));
  });

  return searchParams;
};

const getFilenameFromContentDisposition = (header?: string) => {
  if (!header) return null;

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const simpleMatch = /filename="?([^"]+)"?/i.exec(header);
  if (simpleMatch?.[1]) {
    return simpleMatch[1];
  }

  return null;
};

// ============================================
// Budget Periods
// ============================================
export const budgetPeriodService = {
  getCurrent: async (): Promise<BudgetPeriod> => {
    const response = await api.get(`${BASE_URL}/budget-periods/current`);
    return response.data;
  },

  getNavigation: async (periodId?: string): Promise<BudgetPeriodNavigation> => {
    const response = await api.get(`${BASE_URL}/budget-periods/navigation`, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  getAll: async (page = 1, pageSize = 12): Promise<PaginatedResponse<BudgetPeriod>> => {
    const response = await api.get(`${BASE_URL}/budget-periods`, {
      params: { page, page_size: pageSize },
    });
    return response.data;
  },

  getById: async (id: string): Promise<BudgetPeriod> => {
    const response = await api.get(`${BASE_URL}/budget-periods/${id}`);
    return response.data;
  },

  getSummary: async (id: string): Promise<BudgetPeriodSummary> => {
    const response = await api.get(`${BASE_URL}/budget-periods/${id}/summary`);
    return response.data;
  },

  create: async (data: BudgetPeriodCreate): Promise<BudgetPeriod> => {
    const response = await api.post(`${BASE_URL}/budget-periods`, data);
    return response.data;
  },

  update: async (id: string, data: BudgetPeriodUpdate): Promise<BudgetPeriod> => {
    const response = await api.put(`${BASE_URL}/budget-periods/${id}`, data);
    return response.data;
  },

  close: async (id: string): Promise<BudgetPeriod> => {
    const response = await api.post(`${BASE_URL}/budget-periods/${id}/close`);
    return response.data;
  },

  /**
   * Saldo do PO anterior aguardando decisão. `null` quando não há pergunta a
   * fazer (saldo zero, sem PO anterior, decisão já tomada ou período fechado).
   */
  getPendingCarryover: async (id: string): Promise<PendingCarryover | null> => {
    const response = await api.get(`${BASE_URL}/budget-periods/${id}/carryover/pending`);
    if (!response.data) return null;

    // O backend serializa Decimal como string ("900.00"). Normalizamos aqui para
    // que o resto do app trabalhe sempre com número — comparações de sinal e
    // formatação silenciosamente erram quando a string escapa da fronteira.
    return { ...response.data, amount: Number(response.data.amount) };
  },

  acceptCarryover: async (id: string): Promise<BudgetPeriod> => {
    const response = await api.post(`${BASE_URL}/budget-periods/${id}/carryover/accept`);
    return response.data;
  },

  declineCarryover: async (id: string): Promise<BudgetPeriod> => {
    const response = await api.post(`${BASE_URL}/budget-periods/${id}/carryover/decline`);
    return response.data;
  },

  navigate: async (
    direction: 'previous' | 'next' | 'current',
    periodId?: string
  ): Promise<BudgetPeriod> => {
    const response = await api.post(`${BASE_URL}/budget-periods/navigate/${direction}`, undefined, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },
  
  copyFromPrevious: async (options: {
    copy_revenues?: boolean;
    copy_future_expenses?: boolean;
    copy_monthly_expenses?: boolean;
    copy_daily_expenses?: boolean;
    revenue_types?: string[];
    expense_types?: string[];
  }): Promise<{
    revenues_copied: number;
    future_expenses_copied: number;
    monthly_expenses_copied: number;
    daily_expenses_copied: number;
    message: string;
  }> => {
    const response = await api.post(`${BASE_URL}/budget-periods/copy-from-previous`, options);
    return response.data;
  },
};

// ============================================
// Agents
// ============================================
export const agentService = {
  getAll: async (includeInactive = false): Promise<Agent[]> => {
    const response = await api.get(`${BASE_URL}/agents`, {
      params: { include_inactive: includeInactive },
    });
    return response.data;
  },

  getById: async (id: string): Promise<Agent> => {
    const response = await api.get(`${BASE_URL}/agents/${id}`);
    return response.data;
  },

  create: async (data: AgentCreate): Promise<Agent> => {
    const response = await api.post(`${BASE_URL}/agents`, data);
    return response.data;
  },

  update: async (id: string, data: AgentUpdate): Promise<Agent> => {
    const response = await api.put(`${BASE_URL}/agents/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${BASE_URL}/agents/${id}`);
  },
};

// ============================================
// Financial Lists
// ============================================
export const financialListService = {
  getAll: async (): Promise<FinancialListItem[]> => {
    const response = await api.get(`${BASE_URL}/lists`);
    return response.data;
  },

  getByType: async (listType: FinancialListType): Promise<FinancialListResponse> => {
    const response = await api.get(`${BASE_URL}/lists/${listType}`);
    return response.data;
  },

  createItem: async (listType: FinancialListType, data: FinancialListItemCreate): Promise<FinancialListItem> => {
    const response = await api.post(`${BASE_URL}/lists/${listType}/items`, data);
    return response.data;
  },

  updateItem: async (itemId: string, data: Partial<FinancialListItem>): Promise<FinancialListItem> => {
    const response = await api.put(`${BASE_URL}/lists/items/${itemId}`, data);
    return response.data;
  },

  deleteItem: async (itemId: string): Promise<void> => {
    await api.delete(`${BASE_URL}/lists/items/${itemId}`);
  },
};

// ============================================
// Investment Institutions (Local do investimento)
// ============================================
export const investmentInstitutionService = {
  getAll: async (params?: { include_inactive?: boolean }): Promise<InvestmentInstitution[]> => {
    const response = await api.get(`${BASE_URL}/investment-institutions`, { params });
    return response.data;
  },

  create: async (data: InvestmentInstitutionCreate): Promise<InvestmentInstitution> => {
    const response = await api.post(`${BASE_URL}/investment-institutions`, data);
    return response.data;
  },

  update: async (id: string, data: InvestmentInstitutionUpdate): Promise<InvestmentInstitution> => {
    const response = await api.put(`${BASE_URL}/investment-institutions/${id}`, data);
    return response.data;
  },
};

// ============================================
// Revenues
// ============================================
export const revenueService = {
  getAll: async (periodId?: string): Promise<Revenue[]> => {
    const response = await api.get(`${BASE_URL}/revenues`, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  /** Receitas pendentes cuja data prevista de recebimento já passou. */
  getOverdue: async (periodId?: string): Promise<Revenue[]> => {
    const response = await api.get(`${BASE_URL}/revenues/overdue`, {
      params: periodId ? { budget_period_id: periodId } : undefined,
    });
    return response.data;
  },

  /** Receitas pendentes a receber nos próximos `daysAhead` dias. */
  getUpcoming: async (daysAhead: number, periodId?: string): Promise<Revenue[]> => {
    const response = await api.get(`${BASE_URL}/revenues/upcoming`, {
      params: { days_ahead: daysAhead, ...(periodId ? { budget_period_id: periodId } : {}) },
    });
    return response.data;
  },

  getById: async (id: string): Promise<Revenue> => {
    const response = await api.get(`${BASE_URL}/revenues/${id}`);
    return response.data;
  },

  getAnalytics: async (id: string): Promise<{ average: number; trend: string; history: number[] }> => {
    const response = await api.get(`${BASE_URL}/revenues/${id}/analytics`);
    return response.data;
  },

  create: async (data: RevenueCreate, periodId?: string): Promise<Revenue> => {
    const response = await api.post(`${BASE_URL}/revenues`, data, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  update: async (id: string, data: RevenueUpdate): Promise<Revenue> => {
    const response = await api.put(`${BASE_URL}/revenues/${id}`, data);
    return response.data;
  },

  delete: async (id: string, params?: { apply_to_future?: boolean }): Promise<void> => {
    await api.delete(`${BASE_URL}/revenues/${id}`, { params });
  },

  realize: async (id: string, data: RevenueRealization): Promise<Revenue> => {
    const response = await api.post(`${BASE_URL}/revenues/${id}/realize`, data);
    return response.data;
  },

  addFractionalRealization: async (id: string, data: FractionalRealizationCreate): Promise<Revenue> => {
    const response = await api.post(`${BASE_URL}/revenues/${id}/fractional-realization`, data);
    return response.data;
  },

  updateFractionalRealization: async (
    revenueId: string,
    realizationId: string,
    data: FractionalRealizationCreate
  ): Promise<Revenue> => {
    const response = await api.put(
      `${BASE_URL}/revenues/${revenueId}/fractional-realization/${realizationId}`,
      data
    );
    return response.data;
  },

  deleteFractionalRealization: async (revenueId: string, realizationId: string): Promise<Revenue> => {
    const response = await api.delete(
      `${BASE_URL}/revenues/${revenueId}/fractional-realization/${realizationId}`
    );
    return response.data;
  },

  undoRealization: async (id: string): Promise<Revenue> => {
    const response = await api.post(`${BASE_URL}/revenues/${id}/undo-realization`);
    return response.data;
  },

  copyFromPrevious: async (params?: {
    target_period_id?: string;
    source_period_id?: string;
    revenue_types?: string[];
    copy_data?: 'planned' | 'realized' | 'both';
    revenue_ids?: string[];
  }): Promise<{ copied_count: number; revenues: Revenue[] }> => {
    const response = await api.post(`${BASE_URL}/revenues/copy-from-previous`, undefined, {
      params: buildQueryParams(params),
    });
    return response.data;
  },

  getCopySourcePeriods: async (params: {
    target_period_id: string;
    copy_data?: 'planned' | 'realized' | 'both';
  }): Promise<BudgetPeriod[]> => {
    const response = await api.get(`${BASE_URL}/revenues/copy-source-periods`, { params });
    return response.data;
  },
};

// ============================================
// Future Expenses
// ============================================
export const futureExpenseService = {
  getAll: async (status?: string, periodId?: string): Promise<FutureExpense[]> => {
    const response = await api.get(`${BASE_URL}/future-expenses`, {
      params: {
        ...(status ? { status } : {}),
        ...(periodId ? { period_id: periodId } : {}),
      },
    });
    return response.data;
  },

  getAllActive: async (): Promise<FutureExpense[]> => {
    const response = await api.get(`${BASE_URL}/future-expenses/all-active`);
    return response.data;
  },

  getById: async (id: string): Promise<FutureExpense> => {
    const response = await api.get(`${BASE_URL}/future-expenses/${id}`);
    return response.data;
  },

  getCalculation: async (id: string): Promise<FutureExpenseCalculation> => {
    const response = await api.get(`${BASE_URL}/future-expenses/${id}/calculation`);
    return response.data;
  },

  calculate: async (
    data: FutureExpenseCalculationRequest,
    periodId?: string
  ): Promise<FutureExpenseCalculation> => {
    const response = await api.post(`${BASE_URL}/future-expenses/calculate`, data, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  getRealizationSheet: async (id: string): Promise<FutureExpenseRealizationSheet> => {
    const response = await api.get(`${BASE_URL}/future-expenses/${id}/realization-sheet`);
    return response.data;
  },

  create: async (data: FutureExpenseCreate, periodId?: string): Promise<FutureExpense> => {
    const response = await api.post(`${BASE_URL}/future-expenses`, data, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  update: async (id: string, data: FutureExpenseUpdate): Promise<FutureExpense> => {
    const response = await api.put(`${BASE_URL}/future-expenses/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${BASE_URL}/future-expenses/${id}`);
  },

  realize: async (id: string, data: FutureExpenseRealizationCreate): Promise<FutureExpense> => {
    const response = await api.post(`${BASE_URL}/future-expenses/${id}/contribute`, data);
    return response.data;
  },

  getRealizations: async (id: string): Promise<FutureExpenseRealization[]> => {
    const response = await api.get(`${BASE_URL}/future-expenses/${id}/realizations`);
    return response.data;
  },

  updateRealization: async (id: string, realizationId: string, data: FutureExpenseRealizationUpdate): Promise<FutureExpense> => {
    const response = await api.put(`${BASE_URL}/future-expenses/${id}/realizations/${realizationId}`, data);
    return response.data;
  },

  deleteRealization: async (id: string, realizationId: string): Promise<FutureExpense> => {
    const response = await api.delete(`${BASE_URL}/future-expenses/${id}/realizations/${realizationId}`);
    return response.data;
  },

  undoRealization: async (id: string, realizationId: string): Promise<FutureExpense> => {
    const response = await api.post(`${BASE_URL}/future-expenses/${id}/undo-realization`, {
      realization_id: realizationId,
    });
    return response.data;
  },

  replan: async (
    id: string,
    data: { new_target_date?: string; new_target_amount?: number; reason?: string }
  ): Promise<FutureExpense> => {
    const response = await api.post(`${BASE_URL}/future-expenses/${id}/replan`, data);
    return response.data;
  },

  execute: async (id: string, data: FutureExpenseExecuteRequest): Promise<FutureExpenseExecuteResponse> => {
    const response = await api.post(`${BASE_URL}/future-expenses/${id}/execute`, data);
    return response.data;
  },

  pause: async (id: string): Promise<FutureExpense> => {
    const response = await api.post(`${BASE_URL}/future-expenses/${id}/pause`);
    return response.data;
  },

  resume: async (id: string): Promise<FutureExpense> => {
    const response = await api.post(`${BASE_URL}/future-expenses/${id}/resume`);
    return response.data;
  },
};

// ============================================
// Monthly Expenses
// ============================================
export const monthlyExpenseService = {
  getAll: async (periodId?: string): Promise<MonthlyExpense[]> => {
    const response = await api.get(`${BASE_URL}/monthly-expenses`, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  getOverdue: async (budgetPeriodId?: string): Promise<MonthlyExpense[]> => {
    const response = await api.get(`${BASE_URL}/monthly-expenses/overdue`, {
      params: budgetPeriodId ? { budget_period_id: budgetPeriodId } : undefined,
    });
    return response.data;
  },

  getUpcoming: async (daysAhead = 7, budgetPeriodId?: string): Promise<MonthlyExpense[]> => {
    const response = await api.get(`${BASE_URL}/monthly-expenses/upcoming`, {
      params: {
        days_ahead: daysAhead,
        ...(budgetPeriodId ? { budget_period_id: budgetPeriodId } : {}),
      },
    });
    return response.data;
  },

  getById: async (id: string): Promise<MonthlyExpense> => {
    const response = await api.get(`${BASE_URL}/monthly-expenses/${id}`);
    return response.data;
  },

  create: async (data: MonthlyExpenseCreate, periodId?: string): Promise<MonthlyExpense> => {
    const response = await api.post(`${BASE_URL}/monthly-expenses`, data, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  update: async (id: string, data: MonthlyExpenseUpdate): Promise<MonthlyExpense> => {
    const response = await api.put(`${BASE_URL}/monthly-expenses/${id}`, data);
    return response.data;
  },

  delete: async (id: string, params?: { apply_to_future?: boolean }): Promise<void> => {
    await api.delete(`${BASE_URL}/monthly-expenses/${id}`, { params });
  },

  realize: async (id: string, data: MonthlyExpenseRealization): Promise<MonthlyExpense> => {
    const response = await api.post(`${BASE_URL}/monthly-expenses/${id}/realize`, data);
    return response.data;
  },

  undoRealization: async (id: string): Promise<MonthlyExpense> => {
    const response = await api.post(`${BASE_URL}/monthly-expenses/${id}/undo-realization`);
    return response.data;
  },

  copyFromPrevious: async (): Promise<{ copied_count: number; expenses: MonthlyExpense[] }> => {
    const response = await api.post(`${BASE_URL}/monthly-expenses/copy-from-previous`);
    return response.data;
  },
};

// ============================================
// Daily Expenses
// ============================================
export const dailyExpenseService = {
  getAll: async (periodId?: string): Promise<DailyExpense[]> => {
    const response = await api.get(`${BASE_URL}/daily-expenses`, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  getSummaryByCategory: async (): Promise<{ category: string; total: number; count: number }[]> => {
    const response = await api.get(`${BASE_URL}/daily-expenses/summary-by-category`);
    return response.data;
  },

  getSuggestion: async (id: string): Promise<DailySuggestedSpend> => {
    const response = await api.get(`${BASE_URL}/daily-expenses/${id}/suggestion`);
    return response.data;
  },

  getById: async (id: string): Promise<DailyExpense> => {
    const response = await api.get(`${BASE_URL}/daily-expenses/${id}`);
    return response.data;
  },

  create: async (data: DailyExpenseCreate, periodId?: string): Promise<DailyExpense> => {
    const response = await api.post(`${BASE_URL}/daily-expenses`, data, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  update: async (id: string, data: DailyExpenseUpdate): Promise<DailyExpense> => {
    const response = await api.put(`${BASE_URL}/daily-expenses/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${BASE_URL}/daily-expenses/${id}`);
  },

  realize: async (id: string, data: DailyExpenseRealizationCreate): Promise<DailyExpense> => {
    const response = await api.post(`${BASE_URL}/daily-expenses/${id}/realize`, data);
    return response.data;
  },

  updateRealization: async (
    expenseId: string,
    realizationId: string,
    data: DailyExpenseRealizationUpdate
  ): Promise<DailyExpense> => {
    const response = await api.put(
      `${BASE_URL}/daily-expenses/${expenseId}/realizations/${realizationId}`,
      data
    );
    return response.data;
  },

  deleteRealization: async (expenseId: string, realizationId: string): Promise<DailyExpense> => {
    const response = await api.delete(`${BASE_URL}/daily-expenses/${expenseId}/realizations/${realizationId}`);
    return response.data;
  },

  // Backward-compatible alias (old endpoint removed in backend)
  undoRealization: async (id: string, realizationId: string): Promise<DailyExpense> => {
    return dailyExpenseService.deleteRealization(id, realizationId);
  },

  copyFromPrevious: async (params?: {
    target_period_id?: string;
    source_period_id?: string;
    copy_data?: 'planned' | 'realized';
    expense_ids?: string[];
  }): Promise<{ copied_count: number; expenses: DailyExpense[] }> => {
    const response = await api.post(`${BASE_URL}/daily-expenses/copy-from-previous`, undefined, {
      params: buildQueryParams(params),
    });
    return response.data;
  },
  getCopySourcePeriods: async (params: {
    target_period_id: string;
    copy_data?: 'planned' | 'realized';
  }): Promise<BudgetPeriod[]> => {
    const response = await api.get(`${BASE_URL}/daily-expenses/copy-source-periods`, { params });
    return response.data;
  },
};

// ============================================
// Dashboard
// ============================================
export const dashboardService = {
  getAll: async (periodId?: string): Promise<DashboardData> => {
    const response = await api.get(`${BASE_URL}/dashboard`, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  getKPIs: async (periodId?: string): Promise<DashboardKPIs> => {
    const response = await api.get(`${BASE_URL}/dashboard/kpis`, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  getPlannedVsRealized: async (periodId?: string) => {
    const response = await api.get(`${BASE_URL}/dashboard/planned-vs-realized`, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  getBalanceEvolution: async (periodId?: string) => {
    const response = await api.get(`${BASE_URL}/dashboard/balance-evolution`, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  getExpensesByCategory: async (periodId?: string) => {
    const response = await api.get(`${BASE_URL}/dashboard/expenses-by-category`, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  getFutureExpensesProgress: async () => {
    const response = await api.get(`${BASE_URL}/dashboard/future-expenses-progress`);
    return response.data;
  },

  getAlerts: async () => {
    const response = await api.get(`${BASE_URL}/dashboard/alerts`);
    return response.data;
  },

  getRecentTransactions: async (limit = 10, periodId?: string): Promise<RecentTransaction[]> => {
    const response = await api.get(`${BASE_URL}/dashboard/recent-transactions`, {
      params: { limit, period_id: periodId },
    });
    return response.data;
  },

  searchTransactions: async (query: string, periodId?: string): Promise<SearchResults> => {
    const response = await api.get(`${BASE_URL}/dashboard/search`, {
      params: { q: query, period_id: periodId },
    });
    return response.data;
  },
};

// ============================================
// Reports
// ============================================
export const reportsService = {
  getPeriodSummary: async (periodId?: string): Promise<PeriodSummaryReport> => {
    const response = await api.get(`${BASE_URL}/reports/period-summary`, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  getTrendAnalysis: async (periodsBack = 6): Promise<TrendAnalysisReport> => {
    const response = await api.get(`${BASE_URL}/reports/trend-analysis`, {
      params: { periods_back: periodsBack },
    });
    return response.data;
  },

  getExpenseAnalysis: async (periodId?: string): Promise<ExpenseAnalysisReport> => {
    const response = await api.get(`${BASE_URL}/reports/expense-analysis`, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  getRevenueAnalysis: async (periodId?: string): Promise<RevenueAnalysisReport> => {
    const response = await api.get(`${BASE_URL}/reports/revenue-analysis`, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  getCashFlow: async (mode: CashFlowMode, periodId?: string): Promise<CashFlowReport> => {
    const response = await api.get(`${BASE_URL}/reports/cash-flow`, {
      params: {
        mode,
        ...(periodId ? { period_id: periodId } : {}),
      },
    });
    return response.data;
  },

  downloadCashFlowPdf: async (mode: CashFlowMode, periodId?: string) => {
    const response = await api.get(`${BASE_URL}/reports/cash-flow/pdf`, {
      params: {
        mode,
        ...(periodId ? { period_id: periodId } : {}),
      },
      responseType: 'blob',
    });

    return {
      blob: response.data as Blob,
      filename:
        getFilenameFromContentDisposition(response.headers['content-disposition']) ||
        `fluxo_de_caixa_${mode}.pdf`,
    };
  },

  getFutureExpenseSummary: async (periodId?: string): Promise<FutureExpenseSummaryReport> => {
    const response = await api.get(`${BASE_URL}/reports/future-expenses-summary`, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return response.data;
  },

  getExpenseCategoryTimeline: async (
    periodsBack = 6,
    periodId?: string
  ): Promise<ExpenseCategoryTimelineReport> => {
    const response = await api.get(`${BASE_URL}/reports/expense-category-timeline`, {
      params: {
        periods_back: periodsBack,
        ...(periodId ? { period_id: periodId } : {}),
      },
    });
    return response.data;
  },

  getRevenueTypeTimeline: async (
    periodsBack = 6,
    periodId?: string
  ): Promise<RevenueTypeTimelineReport> => {
    const response = await api.get(`${BASE_URL}/reports/revenue-type-timeline`, {
      params: {
        periods_back: periodsBack,
        ...(periodId ? { period_id: periodId } : {}),
      },
    });
    return response.data;
  },

  getFutureExpenseProjection: async (
    monthsAhead = 6,
    periodId?: string
  ): Promise<FutureExpenseProjectionReport> => {
    const response = await api.get(`${BASE_URL}/reports/future-expense-projection`, {
      params: {
        months_ahead: monthsAhead,
        ...(periodId ? { period_id: periodId } : {}),
      },
    });
    return response.data;
  },
};

// ============================================
// User Financial Settings
// ============================================
export const financialSettingsService = {
  get: async (): Promise<UserFinancialSettings> => {
    const response = await api.get(`${BASE_URL}/settings`);
    return response.data;
  },

  update: async (data: UserFinancialSettingsUpdate): Promise<UserFinancialSettings> => {
    const response = await api.put(`${BASE_URL}/settings`, data);
    return response.data;
  },

  reset: async (): Promise<UserFinancialSettings> => {
    const response = await api.post(`${BASE_URL}/settings/reset`);
    return response.data;
  },
};

// Export all services
export default {
  budgetPeriod: budgetPeriodService,
  agent: agentService,
  financialList: financialListService,
  investmentInstitution: investmentInstitutionService,
  revenue: revenueService,
  futureExpense: futureExpenseService,
  monthlyExpense: monthlyExpenseService,
  dailyExpense: dailyExpenseService,
  dashboard: dashboardService,
  settings: financialSettingsService,
};
